import type { AppFile, AppFileEntry, AppFiles } from '~/types/files';
import {
  FILE_SIZE_LIMIT_MB_ANTHROPIC,
  FILE_SIZE_LIMIT_MB_PAID_TIER,
  FILE_SIZE_LIMIT_MB_PERSONAL_TIER,
} from '~/utils/constants';
import type { EffectiveTier } from '~/utils/effectiveTier';

const textEncoder = new TextEncoder();

export function mbToBytes(mb: number) {
  return mb * 1024 * 1024;
}

export function getFileSizeLimitMB({
  tier,
  target,
}: {
  tier: EffectiveTier | undefined;
  target: 'anthropic' | 'bolt';
}) {
  if (!tier) {
    return FILE_SIZE_LIMIT_MB_ANTHROPIC;
  }

  if (target === 'anthropic') {
    return FILE_SIZE_LIMIT_MB_ANTHROPIC;
  }

  if (tier.level === 0) {
    return FILE_SIZE_LIMIT_MB_PERSONAL_TIER;
  }

  return FILE_SIZE_LIMIT_MB_PAID_TIER;
}

export function getFileSizeLimit({ tier, target }: { tier: EffectiveTier | undefined; target: 'anthropic' | 'bolt' }) {
  const mb = getFileSizeLimitMB({ tier, target });

  return {
    bytes: mbToBytes(mb),
    mb,
  };
}

export function getAppFileUnionBySize({
  appFiles,
  limit,
  threshold,
}: {
  appFiles: AppFiles;
  limit: number;
  threshold: number;
}) {
  let totalSize = getFileSize(appFiles);

  if (totalSize <= limit) {
    return { appFiles, largeFiles: [] };
  }

  const filesWithSize = Object.keys(appFiles).map((path) => {
    const file = appFiles[path];
    const contentsSize = file.type === 'file' ? getFileSize(file.contents) : 0;

    return { path, file, contentsSize };
  });

  // smallest files first
  filesWithSize.sort((a, b) => {
    const diff = a.contentsSize - b.contentsSize;

    if (diff === 0) {
      return a.path.localeCompare(b.path);
    }

    return diff;
  });

  const largeFiles: { path: string; file: AppFile }[] = [];

  while (totalSize > limit) {
    const { file, path, contentsSize } = filesWithSize.pop() || {};

    if (file?.type !== 'file' || !path || !contentsSize) {
      break;
    }

    totalSize -= contentsSize + threshold;

    largeFiles.push({ file, path });
  }

  return { appFiles: filesListToFileMap(filesWithSize), largeFiles };
}

function getFileSize(files: unknown) {
  return textEncoder.encode(JSON.stringify(files)).length;
}

function filesListToFileMap(files: { file: AppFileEntry; path: string }[]): AppFiles {
  return files.reduce<AppFiles>((all, current) => {
    all[current.path] = current.file;
    return all;
  }, {});
}
